import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import {
  CallSplitOutlined,
  ErrorOutlineOutlined,
  ListAltOutlined,
  StarOutlineRounded,
} from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { PanelActionFooter } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { designColors, designRadii, designType, designWeights } from "services/constants/designTokens";
import { pfEstablishmentCodeHeader } from "pages/PayRoll/PayRollHeaders";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { DateField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { Required, SimpleTable, ToneChip, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { StatCardGrid, StatutoryList, SwitchLine } from "pages/PayRoll/Shared/statutoryUi";
import { ACTIVE_STATUS_COLORS, CODE_TYPE_LABELS, emptyCode, employeesAt, formatDay, shortCode, validateCode } from "./pfConfigData";
import { saveEstablishmentCode } from "./pfCodesApi";

// Establishment Codes tab (P2) and the Add / Edit PF Code drawer (P2a).
// Employees get their code from their site (Administration > Sites) at
// month end, so a code covers sites, never employees. Codes and sites come
// from the PF API (index.js loads them); a save reloads. The EPFO office is
// typed (stored by name).

const ALL = "all";

// ---------------- Drawer ----------------

const TypeCard = ({ selected, disabled, label, hint, onClick }) => (
  <Mui.ButtonBase
    onClick={onClick}
    disabled={disabled}
    sx={{
      flex: 1,
      justifyContent: "flex-start",
      gap: 1,
      px: 1.5,
      py: 1.25,
      borderRadius: designRadii.card,
      border: `1px solid ${selected ? designColors.teal : designColors.slate300}`,
      backgroundColor: selected ? designColors.tealTint : designColors.white,
      opacity: disabled && !selected ? 0.6 : 1,
      textAlign: "left",
    }}
  >
    <Mui.Checkbox size="small" checked={selected} disabled={disabled} tabIndex={-1} sx={{ p: 0, "&.Mui-checked": { color: designColors.tealDark } }} />
    <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: selected ? designWeights.semibold : designWeights.medium }}>
      {label}
      {hint ? <Mui.Box component="span" sx={{ ...designType.captionText, ml: 0.5 }}>({hint})</Mui.Box> : null}
    </Mui.Typography>
  </Mui.ButtonBase>
);

// "Sites covered": every site with a tick. A site held by another active
// code, or a switched-off site not ticked already, can't be ticked.
const SitesCovered = ({ sites, value, onChange, codeId, error }) => {
  if (!sites.length) {
    return <RequestInfoBanner tone="warning" body="No sites yet. Add the company's sites in Administration › Sites first." />;
  }
  const toggle = (id) => onChange(value.includes(id) ? value.filter((item) => item !== id) : [...value, id]);
  return (
    <Mui.Box sx={error ? { "& > div": { borderColor: "#DC2626" } } : undefined}>
      <SimpleTable fit heads={[{ label: "", width: 44 }, { label: "Site", width: 240 }, { label: "Employees", width: 100, align: "right" }]}>
        {sites.map((site) => {
          const checked = value.includes(site.id);
          const owner = site.linked_code_id != null && site.linked_code_id !== codeId ? site.linked_code : null;
          const disabled = Boolean(owner) || (!site.is_active && !checked);
          const hint = owner ? `In ${owner}` : !site.is_active ? "Inactive site" : "";
          return (
            <Mui.TableRow
              key={site.id}
              hover={!disabled}
              onClick={disabled ? undefined : () => toggle(site.id)}
              sx={{ cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.6 : 1 }}
            >
              <Mui.TableCell sx={{ ...tableCellSx, py: 0.5 }}>
                <Mui.Checkbox
                  size="small"
                  checked={checked}
                  disabled={disabled}
                  onClick={(event) => event.stopPropagation()}
                  onChange={() => toggle(site.id)}
                  inputProps={{ "aria-label": site.name }}
                  sx={{ p: 0.5, "&.Mui-checked": { color: designColors.tealDark } }}
                />
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.medium }}>{site.name}</Mui.Typography>
                {hint ? <Mui.Typography sx={designType.captionText}>{hint}</Mui.Typography> : null}
              </Mui.TableCell>
              <Mui.TableCell sx={{ ...tableCellSx, textAlign: "right" }}>{site.employees.toLocaleString("en-IN")}</Mui.TableCell>
            </Mui.TableRow>
          );
        })}
      </SimpleTable>
    </Mui.Box>
  );
};

// Form key -> error key (the server's field-error keys).
const ERROR_KEY = { epfo_office_id: "epfo_office", site_ids: "sites" };
const SERVER_FIELDS = ["code", "type", "branch_name", "coverage_from", "epfo_office", "sites", "status"];

const CodePanel = ({ open, initial, codes, sites, domain, companyId, companyName, onClose, onSaved }) => {
  const [form, setForm] = useState(initial);
  const [showErrors, setShowErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initial);
    setShowErrors(false);
    setServerErrors({});
  }, [open, initial]);

  if (!form) return null;
  const editing = form.id != null;
  const mainCode = codes.find((code) => code.type === "MAIN");
  const mainTaken = Boolean(mainCode && mainCode.id !== form.id);
  const errors = validateCode(form, codes, sites);
  const error = (key) => (showErrors ? errors[key] : undefined) || serverErrors[key];
  const set = (key) => (value) => {
    setForm((current) => ({ ...current, [key]: value }));
    const errorKey = ERROR_KEY[key] || key;
    setServerErrors((current) => (current[errorKey] ? { ...current, [errorKey]: undefined } : current));
  };
  const members = employeesAt(form.site_ids, sites);
  // The office is typed, never picked: a typed name is sent by name (the
  // server stores it, or links the listed EPFO office with that name).
  const setOffice = (name) => {
    setForm((current) => ({ ...current, epfo_office_id: "", epfo_office: name }));
    setServerErrors((current) => (current.epfo_office ? { ...current, epfo_office: undefined } : current));
  };

  const save = async () => {
    setShowErrors(true);
    if (Object.keys(errors).length) return;
    setSaving(true);
    setServerErrors({});
    try {
      const { message } = await saveEstablishmentCode({ domain, companyId, form });
      showToast("success", message || (editing ? `${form.code} updated.` : `${form.code} added.`));
      onClose();
      onSaved();
    } catch (err) {
      if (err.code === "STALE_VERSION") {
        showToast("error", "Someone else changed this code. Reloading…");
        onClose();
        onSaved();
        return;
      }
      const all = err.fieldErrors || {};
      const known = Object.fromEntries(Object.entries(all).filter(([key]) => SERVER_FIELDS.includes(key)));
      setServerErrors(known);
      // Anything not shown on a field (unknown field, server down) goes to a toast.
      if (!Object.keys(known).length || Object.keys(known).length !== Object.keys(all).length) {
        showToast("error", err.message || "Couldn't save the code. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title={editing ? `Edit ${form.code}` : "Add PF Code"}
      subtitle={companyName}
      bodySx={panelBodySx}
      footer={<PanelActionFooter onCancel={onClose} primaryLabel="Save" primaryProps={{ onClick: save, loading: saving, disabled: saving }} />}
    >
      <Mui.Stack spacing={2}>
        <FormSection title="Code Type" info={editing ? "The code type can't be changed after it is added." : "One main code per company; branches use extension codes under it."}>
          <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ gridColumn: "1 / -1" }}>
            <TypeCard
              label="Main code"
              hint={mainTaken ? "already added" : undefined}
              selected={form.type === "MAIN"}
              disabled={mainTaken || editing}
              onClick={() => set("type")("MAIN")}
            />
            <TypeCard label="Extension code" selected={form.type === "EXTENSION"} disabled={editing && form.type === "MAIN"} onClick={() => set("type")("EXTENSION")} />
          </Mui.Stack>
          {error("type") ? <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", gridColumn: "1 / -1" }}>{error("type")}</Mui.Typography> : null}
        </FormSection>

        <FormSection title="Code Details" info="Enter the code exactly as EPFO shows it.">
          {form.type === "EXTENSION" && mainCode ? (
            <Field wide label="Main code" helper="The company's main PF code. Extensions sit under it.">
              <TextField value={`${mainCode.code} — ${mainCode.branch_name.replace(/^.*—\s*/, "")}`} onChange={() => {}} disabled />
            </Field>
          ) : null}
          <Field wide label={<>Establishment code<Required /></>} helper="Exactly as on the EPFO portal, e.g. TNCBE0012345002." error={error("code")}>
            <TextField value={form.code} onChange={(value) => set("code")(value.toUpperCase())} error={error("code")} placeholder="TNCBE0012345002" />
          </Field>
          <Field wide label={<>Branch name<Required /></>} helper="Name shown in lists, ECR files and challans." error={error("branch_name")}>
            <TextField value={form.branch_name} onChange={set("branch_name")} error={error("branch_name")} placeholder="e.g. Coimbatore Depot" />
          </Field>
          <Field label={<>Coverage from<Required /></>} helper="Date PF coverage started." error={error("coverage_from")}>
            <DateField value={form.coverage_from} onChange={set("coverage_from")} error={error("coverage_from")} />
          </Field>
          <Field label={<>EPFO regional office<Required /></>} helper="EPFO office this code is registered with." error={error("epfo_office")}>
            <TextField value={form.epfo_office || ""} onChange={setOffice} error={error("epfo_office")} placeholder="e.g. Coimbatore" />
          </Field>
          <Field wide label="Address" helper="Branch address as registered with EPFO. Optional.">
            <TextField value={form.address} onChange={set("address")} placeholder="Branch address" />
          </Field>
        </FormSection>

        <FormSection title="Sites covered" info="Every employee of this company at these sites is filed under this code.">
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            <SitesCovered sites={sites} value={form.site_ids} onChange={set("site_ids")} codeId={form.id} error={error("sites")} />
            {error("sites") ? <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", mt: 0.75 }}>{error("sites")}</Mui.Typography> : null}
            <Mui.Typography sx={{ ...designType.captionText, mt: 0.75 }}>
              {members ? `${members} employees work at these sites and will use this code from ${formatDay(form.coverage_from)}. ` : ""}
              A site can be in only one PF establishment at a time. Sites come from Administration › Sites.
            </Mui.Typography>
          </Mui.Box>
        </FormSection>

        <FormSection title="Portal" info="For guided ECR filing only. Cauver never stores portal passwords.">
          <Field wide label="Portal login owner" helper="Person who logs in to the EPFO portal. Optional.">
            <TextField value={form.portal_login_owner} onChange={set("portal_login_owner")} placeholder="e.g. R. Karthik · HR Manager" />
          </Field>
        </FormSection>

        <FormSection title="Status" info="Inactive codes link no locations and are left out of new ECR files.">
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            <Mui.Box sx={{ ml: -1.5 }}>
              <SwitchLine checked={form.status === "Active"} onChange={(checked) => set("status")(checked ? "Active" : "Inactive")} label={form.status} />
            </Mui.Box>
            {error("status") ? <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626" }}>{error("status")}</Mui.Typography> : null}
          </Mui.Box>
        </FormSection>
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

// ---------------- Tab ----------------

// addRequest: bumped by the page header's "Add Code" button (top right, like
// the Team Workspace Approve screens) to open the Add PF Code panel.
export const PfEstablishmentCodesTab = ({ domain, companyId, data, onReload, addRequest = 0 }) => {
  const { userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const { codes, sites = [], unlinked } = data;
  const [card, setCard] = useState(ALL);
  const [panel, setPanel] = useState({ open: false, initial: null });

  // Only clicks made while this tab is open count (it unmounts on a tab switch).
  const seenAddRequest = useRef(addRequest);
  useEffect(() => {
    if (addRequest === seenAddRequest.current) return;
    seenAddRequest.current = addRequest;
    setPanel({ open: true, initial: emptyCode(codes) });
    // Only a new click opens it, not a change of the code list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addRequest]);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "pf_establishment_codes_saved_views",
    fullHeaders: pfEstablishmentCodeHeader,
  });

  const mainCode = codes.find((code) => code.type === "MAIN");
  const allRows = useMemo(
    () =>
      // Main code first, its extensions under it.
      [...codes]
        .sort((a, b) => (a.type === b.type ? a.code.localeCompare(b.code) : a.type === "MAIN" ? -1 : 1))
        .map((code) => ({
          ...code,
          type_label: CODE_TYPE_LABELS[code.type],
          main_code_label: code.type === "MAIN" ? "—" : shortCode(mainCode?.code),
          sites_label: code.sites.join(", ") || "—",
        })),
    [codes, mainCode],
  );
  const rows = card === "MAIN" || card === "EXTENSION" ? allRows.filter((row) => row.type === card) : allRows;

  const cards = [
    { key: ALL, title: "All codes", value: allRows.length, icon: ListAltOutlined, tone: "blue" },
    { key: "MAIN", title: "Main code", value: allRows.filter((row) => row.type === "MAIN").length, icon: StarOutlineRounded, tone: "purple" },
    { key: "EXTENSION", title: "Extension codes", value: allRows.filter((row) => row.type === "EXTENSION").length, icon: CallSplitOutlined, tone: "pink" },
    { key: "unlinked", title: "Sites not linked", value: unlinked.length, icon: ErrorOutlineOutlined, tone: "red", filter: false },
  ];

  const renderCell = (row, key) => {
    switch (key) {
      case "code":
        return row.type === "MAIN" ? (
          <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.heading }}>{row.code}</Mui.Typography>
        ) : (
          <Mui.Stack direction="row" spacing={0.75} alignItems="center">
            <Mui.Box component="span" sx={{ color: designColors.slate400 }}>{"└"}</Mui.Box>
            <span>{row.code}</span>
          </Mui.Stack>
        );
      case "type_label":
        return <ToneChip label={row.type_label} tone={row.type === "MAIN" ? "blue" : "purple"} />;
      case "coverage_from":
        return formatDay(row.coverage_from);
      case "members":
        return row.members.toLocaleString("en-IN");
      case "status":
        return <RequestStatusChip status={row.status} colorCode={ACTIVE_STATUS_COLORS[row.status]} />;
      default:
        return row[key] || "—";
    }
  };

  return (
    <>
      <StatCardGrid cards={cards} value={card} onChange={setCard} />
      <StatutoryList
        title="PF Establishment Codes"
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={rows}
        renderCell={renderCell}
        onRowClick={(row) => setPanel({ open: true, initial: codes.find((code) => code.id === row.id) })}
        itemLabel="codes"
        searchPlaceholder="Search code, branch or site"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
      />
      <Mui.Stack spacing={1.5} sx={{ mt: 2 }}>
        {unlinked.map((site) => (
          <RequestInfoBanner
            key={site.id}
            tone="warning"
            body={`Site “${site.name}” (${site.employees} employees) is not in any PF code. Add it to a code, or its employees cannot be included in an ECR file.`}
          />
        ))}
        <RequestInfoBanner body="One ECR file and one challan per code each month. Most companies have only a main code and file everything under it." />
      </Mui.Stack>

      <CodePanel
        open={panel.open}
        initial={panel.initial}
        codes={codes}
        sites={sites}
        domain={domain}
        companyId={companyId}
        companyName={topbarCompanyName}
        onClose={() => setPanel((current) => ({ ...current, open: false }))}
        onSaved={onReload}
      />
    </>
  );
};
